import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, User, Trash, Key } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { DataTable, FilterBar, FilterChip, StatusBadge, DetailDrawer, BulkActionBar } from '../../components/admin/AdminComponents';
import { DeleteConfirmModal } from '../../components/DeleteConfirmModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState([]);
  const [drawerUser, setDrawerUser] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);

  const handleSendResetLink = async () => {
    if (!resetTarget) return;
    setSendingLink(true);
    try {
      await axios.post(
        `${API_URL}/api/admin-console/users/${resetTarget.id}/send-reset-link`,
        {},
        { withCredentials: true }
      );
      toast.success(`Reset link emailed to ${resetTarget.email}`);
      setResetTarget(null);
      setNewPassword('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Email send failed');
    } finally {
      setSendingLink(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetTarget) return;
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setResetting(true);
    try {
      await axios.post(
        `${API_URL}/api/admin-console/users/${resetTarget.id}/reset-password`,
        { new_password: newPassword },
        { withCredentials: true }
      );
      toast.success(`Password reset for ${resetTarget.email}`);
      setResetTarget(null);
      setNewPassword('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  const handleDelete = async ({ reason, hardDelete }) => {
    if (!deleteTarget) return;
    try {
      const url = `${API_URL}/api/admin/deletion/users/${deleteTarget.id}${hardDelete ? '?force=true' : ''}`;
      await axios.delete(url, {
        data: { confirm: true, reason },
        withCredentials: true,
      });
      toast.success(`User ${hardDelete ? 'permanently deleted' : 'deactivated'}`);
      setDeleteTarget(null);
      setDrawerUser(null);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  // Admin-triggered reset — clears the user's stored EULA + AI consent + onboarding
  // progress so the user re-sees all consent modals on their next sign-in. Backed
  // by `POST /api/admin-console/users/{id}/reset-onboarding`.
  const resetOnboarding = async (userId) => {
    if (!window.confirm("Reset this user's EULA, AI consent, and onboarding? They'll be re-prompted on next sign-in.")) return;
    try {
      await axios.post(`${API_URL}/api/admin-console/users/${userId}/reset-onboarding`, {}, { withCredentials: true });
      toast.success('Onboarding reset. User will be re-prompted on next sign-in.');
      // Refresh drawer state so the Consent rows update.
      const { data } = await axios.get(`${API_URL}/api/admin-console/users/${userId}`, { withCredentials: true });
      setDrawerUser(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    }
  };

  const fetchUsers = async (p = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (roleFilter) params.append('role', roleFilter);
      params.append('skip', p * 50);
      params.append('limit', '50');
      const { data } = await axios.get(`${API_URL}/api/admin-console/users?${params}`, { withCredentials: true });
      setUsers(data.users || []); setTotal(data.total || 0);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, [roleFilter, page]);

  const openDetail = async (row) => {
    setDrawerLoading(true); setDrawerUser(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin-console/users/${row.id}`, { withCredentials: true });
      setDrawerUser(data);
    } catch { toast.error('Failed to load user details'); }
    finally { setDrawerLoading(false); }
  };

  const updateStatus = async (uid, status) => {
    try {
      await axios.put(`${API_URL}/api/admin-console/users/${uid}/status`, { status }, { withCredentials: true });
      toast.success(`User ${status}`);
      if (drawerUser?.id === uid) setDrawerUser({ ...drawerUser, status });
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const updateRole = async (uid, role) => {
    try {
      await axios.put(`${API_URL}/api/admin-console/users/${uid}/role`, { role }, { withCredentials: true });
      toast.success(`Role updated to ${role}`);
      if (drawerUser?.id === uid) setDrawerUser({ ...drawerUser, role });
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const columns = [
    {
      key: 'name', label: 'User',
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-white/5 flex items-center justify-center text-[10px] text-[#888] font-bold">
            {(row.name || '?')[0].toUpperCase()}
          </div>
          <div>
            <p className="text-[13px] text-white font-medium leading-tight">{row.name}</p>
            <p className="text-[10px] text-[#444]">@{row.username}</p>
          </div>
        </div>
      ),
    },
    { key: 'email', label: 'Email', cellClass: 'hidden md:table-cell', render: v => <span className="text-[12px] text-[#666]">{v}</span> },
    { key: 'role', label: 'Role', render: v => <StatusBadge status={v} size="xs" /> },
    { key: 'status', label: 'Status', render: v => <StatusBadge status={v || 'active'} size="xs" /> },
    { key: 'level', label: 'Level', cellClass: 'hidden lg:table-cell', render: v => <span className="text-[12px] text-[#666] tabular-nums">Lv.{v}</span> },
    { key: 'total_games', label: 'Games', cellClass: 'hidden lg:table-cell', render: v => <span className="text-[12px] text-[#666] tabular-nums">{v}</span> },
    {
      key: 'created_at', label: 'Joined', cellClass: 'hidden xl:table-cell',
      render: v => <span className="text-[11px] text-[#444]">{v ? new Date(v).toLocaleDateString() : '-'}</span>,
    },
    {
      key: '_actions', label: '',
      render: (_, row) => (
        <div className="flex justify-end" onClick={e => e.stopPropagation()}>
          {!row.is_deleted && row.role !== 'admin' && (
            <button
              onClick={() => setDeleteTarget(row)}
              className="text-[11px] px-2 py-1 rounded-md bg-[#EF4444]/10 text-[#EF4444] font-medium hover:bg-[#EF4444]/20 inline-flex items-center gap-1"
              data-testid={`delete-user-row-${row.id}`}
              aria-label={`Delete ${row.name || row.email}`}
            >
              <Trash size={11} weight="bold" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div data-testid="admin-users-page">
      <div className="mb-5">
        <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Users</h1>
        <p className="text-[13px] text-[#444] mt-0.5">{total} registered users</p>
      </div>

      <FilterBar searchValue={search} onSearchChange={setSearch} onSearch={() => { setPage(0); fetchUsers(0); }}
        searchPlaceholder="Search name, email, username..." count={total} label="users">
        {['', 'admin', 'moderator', 'user'].map(r => (
          <FilterChip key={r} label={r || 'All'} active={roleFilter === r}
            onClick={() => { setRoleFilter(r); setPage(0); }} testId={`filter-role-${r || 'all'}`} />
        ))}
      </FilterBar>

      <BulkActionBar count={selected.length} onClear={() => setSelected([])}
        actions={[
          { label: 'Suspend', color: '#F59E0B', onClick: () => selected.forEach(id => updateStatus(id, 'suspended')) },
        ]}
      />

      <DataTable columns={columns} data={users} loading={loading} emptyMessage="No users found" emptyIcon={Users}
        selectable selectedIds={selected} onSelectionChange={setSelected}
        onRowClick={openDetail} rowTestIdPrefix="user"
        page={page} pageSize={50} total={total} onPageChange={setPage}
      />

      {/* Detail Drawer */}
      <DetailDrawer open={!!drawerUser || drawerLoading} onClose={() => setDrawerUser(null)} title="User Details">
        {drawerLoading ? (
          <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-8 bg-white/5 rounded animate-pulse" />)}</div>
        ) : drawerUser && (
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-[#00F0FF]/10 flex items-center justify-center text-lg text-[#00F0FF] font-bold">
                {(drawerUser.name || '?')[0].toUpperCase()}
              </div>
              <div>
                <p className="text-white font-semibold">{drawerUser.name}</p>
                <p className="text-[12px] text-[#555]">{drawerUser.email}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/[0.02] rounded-md p-3">
                <p className="text-[10px] text-[#444] uppercase mb-1">Role</p>
                <StatusBadge status={drawerUser.role} />
              </div>
              <div className="bg-white/[0.02] rounded-md p-3">
                <p className="text-[10px] text-[#444] uppercase mb-1">Status</p>
                <StatusBadge status={drawerUser.status || 'active'} />
              </div>
              <div className="bg-white/[0.02] rounded-md p-3">
                <p className="text-[10px] text-[#444] uppercase mb-1">Level</p>
                <p className="text-white text-sm font-medium">Lv.{drawerUser.level} ({drawerUser.xp} XP)</p>
              </div>
              <div className="bg-white/[0.02] rounded-md p-3">
                <p className="text-[10px] text-[#444] uppercase mb-1">Games / Wins</p>
                <p className="text-white text-sm font-medium">{drawerUser.total_games} / {drawerUser.wins}</p>
              </div>
            </div>

            {drawerUser.bio && (
              <div>
                <p className="text-[10px] text-[#444] uppercase mb-1">Bio</p>
                <p className="text-[13px] text-[#888]">{drawerUser.bio}</p>
              </div>
            )}

            {drawerUser.memberships?.length > 0 && (
              <div>
                <p className="text-[10px] text-[#444] uppercase mb-2">Memberships</p>
                <div className="space-y-1.5">
                  {drawerUser.memberships.map((m, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2 rounded-md bg-white/[0.02]">
                      <div>
                        <p className="text-[12px] text-white">{m.tenant_name}</p>
                        <p className="text-[10px] text-[#444] capitalize">{m.tenant_type}</p>
                      </div>
                      <div className="flex gap-1">
                        {m.roles.map(r => <StatusBadge key={r} status={r} size="xs" />)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Apple App Store compliance audit — show acceptance receipts so you
                can prove to App Review that this exact user agreed to the EULA
                and the third-party AI consent on a specific date. */}
            <div data-testid="admin-user-consent-section">
              <p className="text-[10px] text-[#444] uppercase mb-2">Consent &amp; Onboarding</p>
              <div className="space-y-1.5">
                <ConsentRow
                  label="EULA / Terms of Service"
                  acceptedAt={drawerUser.eula_accepted_at}
                  version={drawerUser.eula_version}
                  ip={drawerUser.eula_accepted_ip}
                  ua={drawerUser.eula_accepted_ua}
                  testid="user-eula-accepted-at"
                />
                <ConsentRow
                  label="AI features (OpenAI GPT-4o)"
                  acceptedAt={drawerUser.ai_consent_granted_at}
                  ip={drawerUser.ai_consent_ip}
                  ua={drawerUser.ai_consent_ua}
                  testid="user-ai-consent-at"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="border-t border-white/[0.06] pt-4 space-y-3">
              <p className="text-[10px] text-[#444] uppercase mb-2">Actions</p>
              <div className="flex flex-wrap gap-2">
                <select value={drawerUser.role}
                  onChange={e => updateRole(drawerUser.id, e.target.value)}
                  className="bg-[#111118] border border-white/[0.08] rounded-md px-3 py-1.5 text-[12px] text-white"
                  data-testid="change-role-select">
                  <option value="user">User</option>
                  <option value="moderator">Moderator</option>
                  <option value="admin">Admin</option>
                </select>
                {(drawerUser.status || 'active') === 'active' && (
                  <button onClick={() => updateStatus(drawerUser.id, 'suspended')}
                    className="px-3 py-1.5 rounded-md bg-[#F59E0B]/10 text-[#F59E0B] text-[12px] font-medium"
                    data-testid="suspend-user-btn">Suspend</button>
                )}
                {drawerUser.status === 'suspended' && (
                  <button onClick={() => updateStatus(drawerUser.id, 'active')}
                    className="px-3 py-1.5 rounded-md bg-[#10B981]/10 text-[#10B981] text-[12px] font-medium"
                    data-testid="activate-user-btn">Activate</button>
                )}
                {drawerUser.status !== 'banned' && (
                  <button onClick={() => updateStatus(drawerUser.id, 'banned')}
                    className="px-3 py-1.5 rounded-md bg-[#EF4444]/10 text-[#EF4444] text-[12px] font-medium"
                    data-testid="ban-user-btn">Ban</button>
                )}
                {!drawerUser.is_deleted && (
                  <button onClick={() => { setResetTarget(drawerUser); setNewPassword(''); }}
                    className="px-3 py-1.5 rounded-md bg-[#9D4CDD]/15 text-[#9D4CDD] text-[12px] font-medium inline-flex items-center gap-1 hover:bg-[#9D4CDD]/25"
                    data-testid="reset-password-btn">
                    <Key size={12} weight="bold" /> Reset Password
                  </button>
                )}
                {!drawerUser.is_deleted && (
                  <button onClick={() => resetOnboarding(drawerUser.id)}
                    className="px-3 py-1.5 rounded-md bg-[#FACC15]/15 text-[#FACC15] text-[12px] font-medium inline-flex items-center gap-1 hover:bg-[#FACC15]/25"
                    data-testid="reset-onboarding-btn">
                    Reset onboarding
                  </button>
                )}
                {!drawerUser.is_deleted && (
                  <button onClick={() => setDeleteTarget(drawerUser)}
                    className="px-3 py-1.5 rounded-md bg-[#EF4444] text-white text-[12px] font-semibold inline-flex items-center gap-1 hover:bg-[#DC2626]"
                    data-testid="delete-user-btn">
                    <Trash size={12} weight="bold" /> Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </DetailDrawer>

      <DeleteConfirmModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        targetType="user"
        targetName={deleteTarget?.email || deleteTarget?.name}
        warning="This will deactivate the user, revoke their session, and prevent login. Historical data (matches, recaps, stats) is preserved. The user can be restored later."
        allowHardDelete={true}
      />

      {/* Admin Password Reset Modal */}
      {resetTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => !resetting && setResetTarget(null)}
          data-testid="reset-password-modal"
        >
          <div
            className="bg-[#0e0e16] border border-white/[0.08] rounded-xl p-5 w-full max-w-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-[#9D4CDD]/15 flex items-center justify-center">
                <Key size={14} weight="bold" className="text-[#9D4CDD]" />
              </div>
              <h3 className="text-white font-bold text-sm">Reset Password</h3>
            </div>
            <p className="text-[#888] text-xs mb-4">
              Set a new password for <span className="text-white font-semibold">{resetTarget.email}</span>,
              or email them a one-time reset link instead.
              Setting a password here clears any pending lockout.
            </p>
            <button
              onClick={handleSendResetLink}
              disabled={sendingLink || resetting}
              className="w-full mb-3 px-3 py-2 rounded-md border border-[#9D4CDD]/40 text-[#9D4CDD] text-xs font-semibold hover:bg-[#9D4CDD]/10 disabled:opacity-40 flex items-center justify-center gap-2"
              data-testid="send-reset-link-btn"
            >
              {sendingLink ? 'Sending email…' : 'Email a Reset Link instead'}
            </button>
            <div className="flex items-center gap-2 mb-3">
              <div className="flex-1 h-px bg-white/[0.08]" />
              <span className="text-[10px] text-[#444] uppercase tracking-wider">or set manually</span>
              <div className="flex-1 h-px bg-white/[0.08]" />
            </div>
            <input
              type="text"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password (min 6 chars)"
              autoFocus
              className="w-full bg-[#111118] border border-white/[0.08] rounded-md px-3 py-2 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#9D4CDD]/60"
              data-testid="new-password-input"
            />
            <div className="flex gap-2 mt-4 justify-end">
              <button
                onClick={() => { setResetTarget(null); setNewPassword(''); }}
                disabled={resetting}
                className="px-4 py-2 rounded-md border border-white/[0.08] text-[#aaa] text-xs hover:bg-white/[0.03] disabled:opacity-50"
                data-testid="reset-password-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleResetPassword}
                disabled={resetting || newPassword.length < 6}
                className="px-4 py-2 rounded-md bg-[#9D4CDD] text-white text-xs font-semibold hover:bg-[#7C3AED] disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="reset-password-confirm"
              >
                {resetting ? 'Resetting…' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Compact row showing whether a user has accepted a given consent (EULA, AI…)
 * and when. Used in the admin user-detail drawer so support can verify
 * acceptance for Apple App Review / GDPR audits.
 */
function ConsentRow({ label, acceptedAt, version, ip, ua, testid }) {
  const accepted = !!acceptedAt;
  const date = accepted ? new Date(acceptedAt) : null;
  const dateStr = date ? date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : null;
  // Compress the user-agent into a short device/OS label so the drawer stays readable.
  const deviceLabel = (() => {
    if (!ua) return null;
    if (/CyBoard\/.*iOS|iPhone/i.test(ua)) return 'iPhone';
    if (/CyBoard\/.*Android|Android/i.test(ua)) return 'Android';
    if (/iPad/i.test(ua)) return 'iPad';
    if (/Mac OS X/i.test(ua)) return 'Mac';
    if (/Windows/i.test(ua)) return 'Windows';
    return 'Web';
  })();
  return (
    <div className="px-3 py-2 rounded-md bg-white/[0.02] space-y-1" data-testid={testid}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[12px] text-white truncate">{label}</p>
          {version && <p className="text-[10px] text-[#444] truncate">v{version}</p>}
        </div>
        {accepted ? (
          <div className="text-right shrink-0">
            <p className="text-[11px] text-[#10B981] font-medium">Accepted</p>
            <p className="text-[10px] text-[#666]">{dateStr}</p>
          </div>
        ) : (
          <p className="text-[11px] text-[#71717A] shrink-0">Pending</p>
        )}
      </div>
      {accepted && (ip || deviceLabel) && (
        <div className="flex items-center gap-2 text-[10px] text-[#666] pt-0.5 border-t border-white/[0.04]">
          {deviceLabel && <span className="px-1.5 py-0.5 rounded bg-white/[0.04] text-[#aaa]">{deviceLabel}</span>}
          {ip && <span className="font-mono text-[#888] truncate" title={ip}>{ip}</span>}
        </div>
      )}
    </div>
  );
}
